<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { Badge } from '../Badge/';
  import { Attribute } from '../Attribute/';
  import { FeedbackMessage } from '../FeedbackMessage/';
  import { Button } from '../Button/';
  import type {
    HighlightCardActionProps,
    HighlightCardProps,
  } from './HighlightCard.types';
  import {
    HighlightCardBackground,
    HighlightCardDescriptionEmphasis,
    HighlightCardLeadingSize,
    HighlightCardStripeTone,
  } from './HighlightCard.types';
  import {
    actionClass,
    backgroundClasses,
    cardClass,
    dataRowClass,
    descriptionClasses,
    footerClass,
    headerActionsClass,
    headerClass,
    headerEndClass,
    headerTitleGroupClass,
    inlineActionClass,
    leadingClass,
    leadingImageClass,
    leadingPreviewImageClass,
    leadingSizeClasses,
    paddedSectionClass,
    sectionClass,
    stripeClass,
    stripeToneClasses,
    titleClass,
  } from './HighlightCard.css';

  const props = withDefaults(defineProps<HighlightCardProps>(), {
    highlighted: false,
    stripeTone: HighlightCardStripeTone.BRAND,
    descriptionEmphasis: HighlightCardDescriptionEmphasis.CAPTION,
    titleLevel: 3,
    padded: false,
    backgroundColor: HighlightCardBackground.BASE,
    leadingSize: HighlightCardLeadingSize.AUTO,
  });

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-highlight-card-${uid}`,
  );

  const emit = defineEmits<{
    action: [index: number];
    headerAction: [index: number];
    inlineAction: [];
    feedbackLinkClick: [];
  }>();

  const cardClasses = computed(() => [
    cardClass,
    backgroundClasses[props.backgroundColor],
  ]);

  const hasHeaderActions = computed(
    () => !!props.headerActions && props.headerActions.length > 0,
  );

  const resolvedActions = computed<HighlightCardActionProps[]>(() => {
    if (props.actions?.length) return props.actions;
    return props.action ? [props.action] : [];
  });

  const hasActions = computed(() => resolvedActions.value.length > 0);

  const hasHeader = computed(
    () =>
      !!props.badge ||
      !!props.title ||
      !!props.description ||
      hasHeaderActions.value ||
      hasActions.value,
  );

  const toButtonProps = ({ label, ...buttonProps }: HighlightCardActionProps) =>
    buttonProps;
</script>

<template>
  <div :class="cardClasses" :data-testid="resolvedTestId">
    <div
      v-if="highlighted"
      :class="[stripeClass, stripeToneClasses[stripeTone]]"
      :data-testid="`${resolvedTestId}-stripe`"
    />

    <div
      v-if="leadingSrc"
      :class="[leadingClass, leadingSizeClasses[leadingSize]]"
      :data-testid="`${resolvedTestId}-leading`"
    >
      <img
        :class="
          leadingSize === HighlightCardLeadingSize.PREVIEW
            ? leadingPreviewImageClass
            : leadingImageClass
        "
        :src="leadingSrc"
        :alt="leadingAlt ?? ''"
        :aria-hidden="!leadingAlt ? 'true' : undefined"
        loading="lazy"
      >
    </div>

    <div :class="[sectionClass, { [paddedSectionClass]: padded }]">
      <div
        v-if="hasHeader"
        :class="headerClass"
        :data-testid="`${resolvedTestId}-header`"
      >
        <Badge v-if="badge" v-bind="badge" />

        <div v-if="title || description" :class="headerTitleGroupClass">
          <component :is="`h${titleLevel}`" v-if="title" :class="titleClass">
            {{ title }}
          </component>
          <span
            v-if="description"
            :class="descriptionClasses[descriptionEmphasis]"
            >{{
              description
            }}</span
          >
        </div>

        <div v-if="hasHeaderActions || hasActions" :class="headerEndClass">
          <div
            v-if="hasHeaderActions"
            :class="headerActionsClass"
            :data-testid="`${resolvedTestId}-header-actions`"
          >
            <Button
              v-for="(headerAction, index) in headerActions"
              :key="index"
              v-bind="toButtonProps(headerAction)"
              @click="emit('headerAction', index)"
            >
              <template v-if="headerAction.label">
                {{ headerAction.label }}
              </template>
            </Button>
          </div>

          <div
            v-if="hasActions"
            :class="actionClass"
            :data-testid="`${resolvedTestId}-action`"
          >
            <Button
              v-for="(cardAction, index) in resolvedActions"
              :key="index"
              v-bind="toButtonProps(cardAction)"
              @click="emit('action', index)"
            >
              <template v-if="cardAction.label">{{
                cardAction.label
              }}</template>
            </Button>
          </div>
        </div>
      </div>

      <div
        v-if="attribute"
        :class="dataRowClass"
        :data-testid="`${resolvedTestId}-data`"
      >
        <Attribute v-bind="attribute" />
      </div>

      <div
        v-if="inlineAction"
        :class="inlineActionClass"
        :data-testid="`${resolvedTestId}-inline-action`"
      >
        <Button
          v-bind="toButtonProps(inlineAction)"
          @click="emit('inlineAction')"
        >
          <template v-if="inlineAction.label">
            {{ inlineAction.label }}
          </template>
        </Button>
      </div>

      <div
        v-if="$slots.footer"
        :class="footerClass"
        :data-testid="`${resolvedTestId}-footer`"
      >
        <slot name="footer" />
      </div>

      <FeedbackMessage
        v-if="feedbackMessage"
        v-bind="feedbackMessage"
        mode="inline"
        @link-click="emit('feedbackLinkClick')"
      />
    </div>
  </div>
</template>
